/**
 * AI Settings Dialog Component
 * Allows users to configure API key and prompt templates
 */

import { useState } from 'react';
import { Eye, EyeOff, Loader2, Check, X, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AiPromptTemplates } from '@/types/ai';
import { testApiKey } from '@/services/ai';
import { DEFAULT_PROMPT_TEMPLATES } from '@/services/aiPrompts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AiSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiKey: string;
  onApiKeyChange: (key: string) => void;
  promptTemplates: AiPromptTemplates;
  onPromptTemplateChange: (key: keyof AiPromptTemplates, value: string) => void;
  onResetPromptTemplates: () => void;
}

type ConnectionStatus = 'idle' | 'testing' | 'success' | 'error';

export function AiSettingsDialog({
  open,
  onOpenChange,
  apiKey,
  onApiKeyChange,
  promptTemplates,
  onPromptTemplateChange,
  onResetPromptTemplates,
}: AiSettingsDialogProps) {
  const [showApiKey, setShowApiKey] = useState(false);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('idle');
  const [activeTab, setActiveTab] = useState<'general' | 'prompts'>('general');

  const handleTestConnection = async () => {
    if (!apiKey) return;

    setConnectionStatus('testing');
    const isValid = await testApiKey(apiKey);
    setConnectionStatus(isValid ? 'success' : 'error');

    // Reset status after a few seconds
    setTimeout(() => {
      setConnectionStatus('idle');
    }, 3000);
  };

  const handleResetTemplate = (key: keyof AiPromptTemplates) => {
    onPromptTemplateChange(key, DEFAULT_PROMPT_TEMPLATES[key]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>AI Settings</DialogTitle>
          <DialogDescription>
            Configure your OpenRouter API key and customize prompt templates.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'general' | 'prompts')}
          className="mt-4"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="prompts">Prompt Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 pt-4">
            {/* API Key */}
            <div className="space-y-2">
              <Label htmlFor="api-key">OpenRouter API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="api-key"
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => onApiKeyChange(e.target.value)}
                    placeholder="sk-or-v1-..."
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">
                      {showApiKey ? 'Hide' : 'Show'} API key
                    </span>
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={!apiKey || connectionStatus === 'testing'}
                  className="w-24"
                >
                  {connectionStatus === 'testing' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : connectionStatus === 'success' ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : connectionStatus === 'error' ? (
                    <X className="h-4 w-4 text-destructive" />
                  ) : (
                    'Test'
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Get your API key from{' '}
                <a
                  href="https://openrouter.ai/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  openrouter.ai/keys
                </a>
                . Your key is stored locally in your browser.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="prompts" className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Customize the system prompts used for each AI step.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={onResetPromptTemplates}
                className="gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                Reset All
              </Button>
            </div>

            {/* Analyze Prompt */}
            <PromptTemplateEditor
              label="Analyze Prompt"
              description="Used when analyzing existing code (modify mode only)"
              value={promptTemplates.analyze}
              onChange={(v) => onPromptTemplateChange('analyze', v)}
              onReset={() => handleResetTemplate('analyze')}
              isDefault={
                promptTemplates.analyze === DEFAULT_PROMPT_TEMPLATES.analyze
              }
            />

            {/* Plan Prompt */}
            <PromptTemplateEditor
              label="Plan Prompt"
              description="Used to create an implementation plan"
              value={promptTemplates.plan}
              onChange={(v) => onPromptTemplateChange('plan', v)}
              onReset={() => handleResetTemplate('plan')}
              isDefault={promptTemplates.plan === DEFAULT_PROMPT_TEMPLATES.plan}
            />

            {/* Implement Prompt */}
            <PromptTemplateEditor
              label="Implement Prompt"
              description="Used to generate OpenSCAD code"
              value={promptTemplates.implement}
              onChange={(v) => onPromptTemplateChange('implement', v)}
              onReset={() => handleResetTemplate('implement')}
              isDefault={
                promptTemplates.implement === DEFAULT_PROMPT_TEMPLATES.implement
              }
            />

            {/* Fix Prompt */}
            <PromptTemplateEditor
              label="Fix Prompt"
              description="Used to fix compilation errors"
              value={promptTemplates.fix}
              onChange={(v) => onPromptTemplateChange('fix', v)}
              onReset={() => handleResetTemplate('fix')}
              isDefault={promptTemplates.fix === DEFAULT_PROMPT_TEMPLATES.fix}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PromptTemplateEditorProps {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  onReset: () => void;
  isDefault: boolean;
}

function PromptTemplateEditor({
  label,
  description,
  value,
  onChange,
  onReset,
  isDefault,
}: PromptTemplateEditorProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <Label>{label}</Label>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        {!isDefault && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onReset}
                className="h-7 w-7"
              >
                <RotateCcw className="h-3 w-3" />
                <span className="sr-only">Reset to default</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reset to default</TooltipContent>
          </Tooltip>
        )}
      </div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className={cn(
          'resize-none font-mono text-xs',
          !isDefault && 'border-primary/50',
        )}
      />
    </div>
  );
}
